import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// The Profile drawer (Fanvue-style push panel) is URL-hash driven:
// "#profile" on ANY route means the drawer is open over that route. Using the
// hash (instead of component state) is what makes the phone's back button /
// back-swipe close the drawer for free, on web and in the Capacitor shell:
// open pushes one history entry, back pops it, and the underlying route never
// changes — so the tab you were on stays mounted with its scroll intact.
export const PROFILE_DRAWER_HASH = "#profile";

export function useProfileDrawer() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOpen = location.hash === PROFILE_DRAWER_HASH;

  const open = useCallback(() => {
    if (location.hash === PROFILE_DRAWER_HASH) return;
    // The state flag marks "this entry was pushed by open()" — close() only
    // dares a history-back when it sees it.
    navigate(location.pathname + location.search + PROFILE_DRAWER_HASH, {
      state: { profileDrawer: true },
    });
  }, [location.hash, location.pathname, location.search, navigate]);

  const close = useCallback(() => {
    if (location.hash !== PROFILE_DRAWER_HASH) return;
    // Entry pushed by open() → back() pops it and lands exactly where the
    // talent was. Any other way of arriving (post-login landing, /profile
    // redirect, direct load, refresh) → the entry below is unknown territory
    // (often /login), so strip the hash in place instead of going back.
    if ((location.state as any)?.profileDrawer && (window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  return { isOpen, open, close };
}
